import { useEffect, useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import CartConflictModal from '../components/CartConflictModal';
import { useCartSync } from '../hooks/useCartSync';

interface PendingReferralReward {
    code: string;
    url: string;
    gift_description: string;
    expires_at: string;
}

export function MainLayout({ children }: { children: React.ReactNode }) {
    // Los pageviews de la SPA se emiten globalmente en app.tsx (#95): acá no
    // va ningún listener de navegación, se duplicaría.

    // Real-time cart sync across devices via Centrifugo
    useCartSync();

    const { pendingReferralReward } = usePage<{ pendingReferralReward?: PendingReferralReward | null }>().props;

    return (
        <div className="flex min-h-screen flex-col">
            <Navbar />
            {pendingReferralReward && (
                <ReferralRewardBanner reward={pendingReferralReward} />
            )}
            <main className="flex-1">{children}</main>
            <Footer />
            <CartConflictModal />
        </div>
    );
}

function ReferralRewardBanner({ reward }: { reward: PendingReferralReward }) {
    const [dismissed, setDismissed] = useState(() => {
        try {
            return sessionStorage.getItem('referral_reward_dismissed') === reward.code;
        } catch {
            return false;
        }
    });

    if (dismissed) return null;

    const dismiss = () => {
        try { sessionStorage.setItem('referral_reward_dismissed', reward.code); } catch {}
        setDismissed(true);
    };

    return (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-base">🎁</span>
                    <span>
                        <strong>¡Tenés un regalo!</strong>{' '}
                        <span className="hidden sm:inline">{reward.gift_description} gratis por referir a un amigo.</span>
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <a
                        href={reward.url}
                        className="rounded-lg bg-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/30 hover:text-white hover:no-underline transition-colors"
                    >
                        Usar regalo
                    </a>
                    <button
                        onClick={dismiss}
                        className="bg-transparent border-none text-white/70 hover:text-white cursor-pointer p-1"
                        aria-label="Cerrar"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
